import { useEffect, type FC } from "react";
import { useMutation, useQuery } from "react-query";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";

import { useTypedSelector } from "../../redux/store";
import { ChatService } from "../../api";
import {
  updateDialogue,
  removeMessageFromDialogue,
  setDialogueOpened
} from "../../redux/slices/privateChat";
import { Chat } from "../../components/Chat";
import { useGetUser } from "../../hooks/useGetUser";
import type {
  DialogueResult,
  UserResult
} from "../../api/generated/data-contracts";
import { createSendingMessage } from "./utils";

export const PrivateChat: FC = () => {
  const messages = useTypedSelector(
    (state) => state.privateChat.openedDialogue?.messages
  );

  const currentUser = useTypedSelector((state) => state.auth.currentUser);
  const userJwt = useTypedSelector((state) => state.auth.value);
  const navigate = useNavigate();
  const params = useParams<{ interlocutorId: string }>();

  const {
    isSuccess: isGetUserSuccess,
    data: userData,
    isLoading: isUserLoading
  } = useGetUser({ auth0Id: params.interlocutorId ?? "" });

  useEffect(() => {
    if (
      (!isUserLoading && !isGetUserSuccess) ||
      params.interlocutorId === currentUser?.auth0Id
    ) {
      navigate("/chat");
    }
  }, [
    isUserLoading,
    userData,
    isGetUserSuccess,
    navigate,
    currentUser,
    params
  ]);

  const dispatch = useDispatch();

  const { isLoading } = useQuery({
    queryKey: [
      "getDialogMessages",
      userData?.auth0Id,
      userJwt,
      params.interlocutorId
    ],
    queryFn: async () => {
      return ChatService.privateDialogueMessagesList(
        { interlocutorId: userData?.auth0Id ?? "" },
        { headers: { Authorization: `Bearer ${userJwt}` } }
      ).then((res) => res.data);
    },
    onSuccess: (data) => {
      dispatch(
        setDialogueOpened({
          userId: data.userId,
          interlocutorId: data.interlocutortId,
          messages: data.paginatedMessages.results
        })
      );
    },
    refetchOnWindowFocus: false,
    enabled: isGetUserSuccess && !!userData?.auth0Id
  });

  const sendMessageMutation = useMutation({
    mutationFn: async ({
      content,
      uuid
    }: {
      content: string;
      uuid: string;
    }) => {
      return ChatService.privateMessageCreate(
        {
          content,
          receiverAuth0Id: params.interlocutorId ?? "",
          uuid
        },
        {
          headers: {
            Authorization: `Bearer ${userJwt}`
          }
        }
      );
    },
    onError: () => {
      toast.error("The message was not sent!");
    }
  });

  const sender: UserResult = {
    userName: currentUser?.userName ?? "",
    firstName: currentUser?.firstName ?? "",
    lastName: currentUser?.lastName ?? "",
    fullName: currentUser?.fullName ?? "",
    auth0Id: currentUser?.auth0Id ?? ""
  };

  const handleSendMessage = (message: string): void => {
    const msg = createSendingMessage(message, sender);
    const dialogue: DialogueResult = {
      lastMessage: msg,
      interlocutorAuth0Id: params.interlocutorId ?? "",
      userAuth0Id: currentUser?.auth0Id ?? "",
      user: sender,
      interlocutor: {
        userName: "",
        firstName: "",
        lastName: "",
        fullName: "",
        auth0Id: ""
      }
    };
    dispatch(updateDialogue(dialogue));
    sendMessageMutation.mutate({ content: message, uuid: msg.uuid });
  };

  const deleteMessageMutation = useMutation({
    mutationFn: async (id: number) => {
      return ChatService.privateMessagesDelete(id, {
        headers: {
          Authorization: `Bearer ${userJwt}`
        }
      });
    },
    onError: () => {
      toast.error("Message was not deleted!");
    },
    onSuccess: (data) => {
      dispatch(
        removeMessageFromDialogue({
          messageId: data.data.id // ????
        })
      );
    }
  });

  const handleDelete = (messageId: number): void => {
    deleteMessageMutation.mutate(messageId);
  };

  return (
    <Chat
      messages={messages ?? []}
      isLoading={isUserLoading || isLoading}
      handleSendMessage={handleSendMessage}
      chatTitle={userData?.fullName ?? userData?.userName ?? "N/A"}
      handleDelete={handleDelete}
      isGlobal={false}
    />
  );
};
